import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { submissionsAPI } from '../services/api';
import { 
  Search, 
  Filter,
  Loader2,
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const statusConfig = {
  draft: { label: 'Draft', icon: FileText, className: 'badge-neutral' },
  submitted: { label: 'Submitted', icon: Clock, className: 'badge-warning' },
  returned: { label: 'Returned', icon: AlertCircle, className: 'badge-info' },
  approved: { label: 'Approved', icon: CheckCircle2, className: 'badge-success' },
  rejected: { label: 'Rejected', icon: XCircle, className: 'badge-error' },
  closed: { label: 'Closed', icon: CheckCircle2, className: 'badge-neutral' },
};

const SubmissionCard = ({ submission, onClick }) => {
  const status = statusConfig[submission.status] || statusConfig.draft;
  const StatusIcon = status.icon;

  return (
    <Card 
      className="cursor-pointer hover:shadow-md transition-all"
      onClick={onClick}
      data-testid={`submission-card-${submission.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className="font-mono text-sm font-bold text-slate-900">
                {submission.submission_number}
              </span>
              <Badge className={status.className}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
            </div>
            <p className="font-medium text-slate-700">
              {submission.template?.name || submission.template_code}
            </p>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
              {submission.vehicle && (
                <span className="font-mono">{submission.vehicle.registration_number}</span>
              )}
              {submission.driver?.user && (
                <span>{submission.driver.user.first_name} {submission.driver.user.last_name}</span>
              )}
              <span>
                {new Date(submission.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
};

export const SubmissionsListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        const params = {};
        if (statusFilter && statusFilter !== 'all') {
          params.status = statusFilter;
        }
        const response = await submissionsAPI.list(params);
        setSubmissions(response.data);
      } catch (error) {
        console.error('Failed to fetch submissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, [statusFilter]);

  const filteredSubmissions = submissions.filter(sub => {
    const searchLower = searchQuery.toLowerCase();
    return sub.submission_number.toLowerCase().includes(searchLower) ||
           sub.template_code.toLowerCase().includes(searchLower) ||
           (sub.vehicle?.registration_number || '').toLowerCase().includes(searchLower) ||
           (sub.driver?.user?.first_name || '').toLowerCase().includes(searchLower) ||
           (sub.driver?.user?.last_name || '').toLowerCase().includes(searchLower);
  });

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    if (value === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', value);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="submissions-list-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            SUBMISSIONS
          </h1>
          <p className="text-slate-500 mt-1">
            View and manage all form submissions
          </p>
        </div>
        <Button 
          onClick={() => navigate('/templates')}
          className="btn-action"
          data-testid="new-submission-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          New Submission
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search by reference, template, vehicle, or driver..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
            data-testid="submissions-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-full md:w-48 h-12" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="returned">Returned</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Submissions List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : filteredSubmissions.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No submissions found</h3>
          <p className="text-slate-500 mt-1">
            {searchQuery || statusFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria'
              : 'Create your first submission to get started'}
          </p>
          <Button 
            onClick={() => navigate('/templates')}
            className="mt-4"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Submission
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSubmissions.map((submission) => (
            <SubmissionCard 
              key={submission.id}
              submission={submission}
              onClick={() => navigate(`/submissions/${submission.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SubmissionsListPage;
